import {Checkbox, DatePicker, Divider, Form, Input} from 'antd';
import {FormComponentProps} from 'antd/lib/form';
import React, {forwardRef, LegacyRef, useRef} from 'react';
import {entryFormOptions, EntryFormProps, useAutoFocus} from 'components/masterdata-2-0/entryForms';
import {ComplianceRuleEntryFormData, ComplianceRuleEntryFormDataValues} from "./types";
import LifetimeExplanationAlert from './lifetime-explanation-alert';
import {VeriflytTicketTemplateMultiSelect} from "../../../../components/form/select/veriflyt-ticket-template-select";

export interface ComplianceRuleEntryFormProps extends EntryFormProps<ComplianceRuleEntryFormData> {

}

const formItemLayout = {
    labelCol: {
        xs: { span: 24 },
        sm: { span: 10 },
    },
    wrapperCol: {
        xs: { span: 24 },
        sm: { span: 14 },
    },
}

const ComplianceRuleEntryForm: React.FC<ComplianceRuleEntryFormProps> = forwardRef(
function ComplianceRuleEntryForm(props, ref: LegacyRef<Form>){
    const nameInputRef = useRef(null);
    const { getFieldDecorator, getFieldError, getFieldValue } = props.form;

    useAutoFocus(props.autoFocus, nameInputRef.current);

    let newEntryDisabledMsg = `Name cannot be changed on existing compliance rules. 
    You must create a new compliance rule first.`;

    if (props.isNewEntry){
        newEntryDisabledMsg = undefined;
    }

    return <Form ref={ref}>
        <Form.Item
            label="Name"
            {...formItemLayout}
        >
            {getFieldDecorator('onScreenName', {
                rules: [
                    { required: true, message: 'Name is required' }
                ]
            })(<Input ref={nameInputRef} disabled={!props.isNewEntry} title={newEntryDisabledMsg} />)}
        </Form.Item>
        <Form.Item label="Short Description" {...formItemLayout}>
            {getFieldDecorator('shortDescription')(
                <Input.TextArea />
            )}
        </Form.Item>
        <Form.Item label="Certifications" {...formItemLayout}>
            {getFieldDecorator('ticketTemplatesThatSatisfy', {
                rules: [
                    { required: true, message: 'Certifications is required' }
                ]
            })(
                <VeriflytTicketTemplateMultiSelect
                    noticeAlertText="If one certification passes, the whole rule will pass."
                />
            )}
        </Form.Item>
        <Divider>Rule lifetime</Divider>
        <Form.Item label="Active" {...formItemLayout}>
            {getFieldDecorator('isActive', {
                initialValue: true,
                valuePropName: 'checked'
            })(<Checkbox />)}
        </Form.Item>
        <Form.Item label="In Effect Date" {...formItemLayout}>
            {getFieldDecorator('inEffectDate', {
                rules: [
                    {
                        validator(rule, value, callback) {
                            const expirationDate = props.form.getFieldValue('expirationDate');
                            if (value && expirationDate && value.isAfter(expirationDate, 'day')) {
                                callback('In Effect Date must be the same day or before Expiration Date');
                            } else {
                                callback();
                            }
                        }
                    }
                ]
            })(<DatePicker />)}
        </Form.Item>
        <Form.Item label="Expiration Date" {...formItemLayout}>
            {getFieldDecorator('expirationDate', {
                rules: [
                    {
                        validator(rule, value, callback) {
                            const inEffectDate = props.form.getFieldValue('inEffectDate');
                            if (value && inEffectDate && value.isBefore(inEffectDate, 'day')) {
                                callback('Expiration Date must be the same day or after In Effect Date');
                            } else {
                                callback();
                            }
                        }
                    }
                ]
            })(<DatePicker />)}
        </Form.Item>
        <LifetimeExplanationAlert form={props.form} />
        {props.dupeChecker}
    </Form>
})

export default Form.create<ComplianceRuleEntryFormProps & FormComponentProps<ComplianceRuleEntryFormDataValues>>(
    entryFormOptions<ComplianceRuleEntryFormProps>()
)(ComplianceRuleEntryForm)